import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";
import { useAppModal } from "components/common/AppModalProvider";
import { WalletWithdrawStatus } from "types";
import { getWithrawList, putApproveWithdraw } from "apis";

const PRESET_REJECT_REASONS = [
    "예금주명과 회원 실명이 일치하지 않습니다.",
    "입력하신 계좌번호가 유효하지 않거나 입금 불가능한 계좌입니다.",
    "주민등록번호 정보가 올바르지 않아 원천징수 세금 신고가 불가능합니다.",
    "기타 (직접 입력)"
];

const WithdrawManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    // Rejection Modal State
    const [rejectModalTarget, setRejectModalTarget] = useState<string | null>(null);
    const [selectedPresetReason, setSelectedPresetReason] = useState<string>(PRESET_REJECT_REASONS[0]);
    const [customRejectReason, setCustomRejectReason] = useState<string>("");

    const itemsPerPage = 10;

    const { data, isLoading, isError } = useQuery(
        ["withdrawList", currentPage, itemsPerPage],
        () => getWithrawList(currentPage, itemsPerPage),
        { keepPreviousData: true }
    );

    if (isLoading) return <div className="p-8"><div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" /></div>;
    if (isError) return <div className="p-8 text-red-500">오류가 발생했습니다.</div>;

    const withdrawList = data?.data.data.items || [];
    const totalItems = data?.data.data.total || 0;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);
    const toggleRow = (id: string) => setExpandedRow(expandedRow === id ? null : id);

    const handleApproveClick = async (withdrawId: string) => {
        if (!(await showConfirm('정산 승인(입금 완료) 처리하시겠습니까?', { title: '정산 승인', confirmText: '확인', variant: 'warning' }))) return;
        try {
            const response = await putApproveWithdraw(withdrawId, WalletWithdrawStatus.ACCEPTED);
            if (response.status === 200) {
                showAlert("정산 승인 처리되었습니다.", { variant: 'success' });
                queryClient.invalidateQueries(["withdrawList"]);
            }
        } catch (error: any) {
            showAlert("정산 승인 처리 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    const handleOpenRejectModal = (withdrawId: string) => {
        setRejectModalTarget(withdrawId);
        setSelectedPresetReason(PRESET_REJECT_REASONS[0]);
        setCustomRejectReason("");
    };

    const handleConfirmReject = async () => {
        if (!rejectModalTarget) return;
        const finalReason = selectedPresetReason === "기타 (직접 입력)"
            ? customRejectReason.trim()
            : selectedPresetReason;

        if (!finalReason) {
            showAlert("거절 사유를 입력해주세요.", { variant: "warning" });
            return;
        }

        try {
            const response = await putApproveWithdraw(rejectModalTarget, WalletWithdrawStatus.REJECTED, finalReason);
            if (response.status === 200) {
                showAlert("정산 거절 처리 및 환불이 완료되었습니다.", { variant: 'success' });
                setRejectModalTarget(null);
                queryClient.invalidateQueries(["withdrawList"]);
            }
        } catch (error: any) {
            showAlert("정산 거절 처리 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    const statusLabel = (status: string) => {
        switch (status) {
            case WalletWithdrawStatus.ACCEPTED: return { text: '승인 완료', cls: 'bg-emerald-50 text-emerald-600' };
            case WalletWithdrawStatus.REJECTED: return { text: '거절됨', cls: 'bg-red-50 text-red-600' };
            default: return { text: '대기 중', cls: 'bg-amber-50 text-amber-600' };
        }
    };

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">출금 관리 (정산)</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}건의 출금 신청 내역</p>
            </div>

            <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden shadow-sm">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-achromatic-100 bg-achromatic-50">
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">#</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">신청자 (실명)</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">주민등록번호</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-right">신청 금액</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-right">세금 (3.3%)</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-right">최종 이체액</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">입금 계좌</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">상태</th>
                            <th className="py-3.5 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">신청일</th>
                        </tr>
                    </thead>
                    <tbody>
                        {withdrawList.map((item: any, index: number) => {
                            const st = statusLabel(item.status);
                            const user = item.user || item.creator?.user || {};
                            const realName = user.realName || user.name || '-';
                            const residentNumber = user.residentNumber || '-';
                            const bankName = item.bankName || user.bankName || '';
                            const accountNumber = item.accountNumber || user.accountNumber || '-';
                            const accountHolder = item.accountHolder || user.accountHolder || '';
                            const tax = item.tax ?? Math.round((item.withdrawAmount || 0) * 0.033);
                            const netAmount = item.netAmount ?? ((item.withdrawAmount || 0) - tax);

                            return (
                                <>
                                    <tr
                                        key={item.id}
                                        className={`border-b border-achromatic-50 cursor-pointer transition-colors hover:bg-primary-50/30
                                            ${expandedRow === item.id ? 'bg-primary-50/20' : ''}`}
                                        onClick={() => toggleRow(item.id)}
                                    >
                                        <td className="py-3.5 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                        <td className="py-3.5 px-4 text-[13px] font-bold text-achromatic-900">
                                            {realName}
                                            <div className="text-[11px] font-normal text-achromatic-400">@{user.profileId || user.name || '유저'}</div>
                                        </td>
                                        <td className="py-3.5 px-4 text-[13px] font-mono text-achromatic-700">{residentNumber}</td>
                                        <td className="py-3.5 px-4 text-[13px] font-bold text-achromatic-900 text-right">
                                            {Number(item.withdrawAmount).toLocaleString()} 원
                                        </td>
                                        <td className="py-3.5 px-4 text-[13px] text-red-500 font-medium text-right">
                                            -{Number(tax).toLocaleString()} 원
                                        </td>
                                        <td className="py-3.5 px-4 text-[13px] font-extrabold text-emerald-600 text-right">
                                            {Number(netAmount).toLocaleString()} 원
                                        </td>
                                        <td className="py-3.5 px-4 text-[13px] text-achromatic-700 font-medium">
                                            <div>{bankName} {accountNumber}</div>
                                            <div className="text-[11px] text-achromatic-400">예금주: {accountHolder || realName}</div>
                                        </td>
                                        <td className="py-3.5 px-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${st.cls}`}>
                                                {st.text}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-4 text-[12px] text-achromatic-400 text-center">
                                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                        </td>
                                    </tr>

                                    {expandedRow === item.id && (
                                        <tr key={`expanded-${item.id}`} className="bg-achromatic-50/50">
                                            <td colSpan={9} className="p-6">
                                                <div className="grid grid-cols-3 gap-4 mb-4">
                                                    {/* 유저 및 세무 정보 */}
                                                    <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                        <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3 flex items-center gap-1">
                                                            👤 세무 및 신청자 정보
                                                        </h3>
                                                        <div className="flex flex-col gap-2 text-[13px]">
                                                            <div className="flex justify-between"><span className="text-achromatic-400">실명</span><span className="font-bold text-achromatic-900">{realName}</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">주민등록번호</span><span className="font-mono font-medium text-achromatic-900">{residentNumber}</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">이메일</span><span className="font-medium text-achromatic-700">{user.email || '-'}</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">연락처</span><span className="font-medium text-achromatic-700">{user.phone || '-'}</span></div>
                                                        </div>
                                                    </div>

                                                    {/* 정산 입금 계좌 정보 */}
                                                    <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                        <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3 flex items-center gap-1">
                                                            🏦 정산 입금 계좌 정보
                                                        </h3>
                                                        <div className="flex flex-col gap-2 text-[13px]">
                                                            <div className="flex justify-between"><span className="text-achromatic-400">입금 은행</span><span className="font-bold text-achromatic-900">{bankName || '-'}</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">계좌 번호</span><span className="font-mono font-bold text-achromatic-900">{accountNumber}</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">예금주</span><span className="font-bold text-achromatic-900">{accountHolder || realName}</span></div>
                                                        </div>
                                                    </div>

                                                    {/* 정산 금액 상세 계산 */}
                                                    <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                        <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3 flex items-center gap-1">
                                                            💵 정산 금액 계산
                                                        </h3>
                                                        <div className="flex flex-col gap-2 text-[13px]">
                                                            <div className="flex justify-between"><span className="text-achromatic-400">신청 출금액</span><span className="font-bold text-achromatic-900">{Number(item.withdrawAmount).toLocaleString()} 원</span></div>
                                                            <div className="flex justify-between"><span className="text-achromatic-400">원천징수세 (3.3%)</span><span className="font-medium text-red-500">-{Number(tax).toLocaleString()} 원</span></div>
                                                            <div className="border-t border-achromatic-100 pt-2 flex justify-between">
                                                                <span className="font-bold text-achromatic-900">실수령 이체액</span>
                                                                <span className="text-lg font-extrabold text-emerald-600">{Number(netAmount).toLocaleString()} 원</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {item.status === WalletWithdrawStatus.REJECTED && item.rejectReason && (
                                                    <div className="mb-4 p-3.5 bg-red-50 border border-red-100 rounded-xl text-[13px] text-red-800">
                                                        <strong className="font-bold">❌ 거절 사유: </strong>{item.rejectReason}
                                                    </div>
                                                )}

                                                <div className="flex justify-between items-center bg-white p-3.5 rounded-xl border border-achromatic-100">
                                                    <span className="text-[12px] text-achromatic-400">
                                                        💡 계좌로 <strong>{Number(netAmount).toLocaleString()}원</strong>을 이체하신 후 [정산 승인]을 누르시거나, 부적합할 경우 [정산 거절]을 선택해 사유를 작성하세요.
                                                    </span>
                                                    {item.status === WalletWithdrawStatus.PENDING && (
                                                        <div className="flex gap-2">
                                                            <button
                                                                className="px-5 py-2.5 bg-emerald-600 text-white text-[13px] font-bold rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
                                                                onClick={(e) => { e.stopPropagation(); handleApproveClick(item.id); }}
                                                            >
                                                                정산 승인 (입금 완료)
                                                            </button>
                                                            <button
                                                                className="px-5 py-2.5 bg-red-500 text-white text-[13px] font-bold rounded-xl hover:bg-red-600 transition-colors shadow-sm"
                                                                onClick={(e) => { e.stopPropagation(); handleOpenRejectModal(item.id); }}
                                                            >
                                                                정산 거절
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                    </tbody>
                </table>

                <div className="p-4 border-t border-achromatic-100 flex justify-center">
                    <ReactPaginate
                        previousLabel={"←"} nextLabel={"→"} breakLabel={"..."} pageCount={pageCount}
                        marginPagesDisplayed={1} pageRangeDisplayed={5} onPageChange={handlePageClick}
                        containerClassName={"flex items-center gap-1"}
                        previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100"}
                        activeClassName={"!bg-primary-500 !text-white"} activeLinkClassName={"text-white"}
                    />
                </div>
            </div>

            {/* 정산 거절 사유 입력 모달 */}
            {rejectModalTarget && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 flex flex-col gap-4 shadow-xl">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-bold text-achromatic-900">❌ 정산 거절 사유 입력</h3>
                            <button onClick={() => setRejectModalTarget(null)} className="text-achromatic-400 hover:text-achromatic-700 text-xl font-bold">×</button>
                        </div>
                        <p className="text-[12px] text-achromatic-400 leading-relaxed">
                            정산을 거절하면 해당 출금 신청 금액은 유저의 지갑 잔액으로 다시 <strong>자동 환불</strong>되며, 입력하신 거절 사유는 유저 알림 및 마이페이지 정산 메뉴에 안내됩니다.
                        </p>

                        <div className="flex flex-col gap-2.5">
                            <label className="text-[12px] font-bold text-achromatic-500">거절 사유 선택</label>
                            {PRESET_REJECT_REASONS.map((reason) => (
                                <label key={reason} className="flex items-center gap-2 text-xs text-achromatic-700 cursor-pointer p-2 rounded-lg hover:bg-achromatic-50">
                                    <input
                                        type="radio"
                                        name="rejectReasonPreset"
                                        checked={selectedPresetReason === reason}
                                        onChange={() => setSelectedPresetReason(reason)}
                                        className="text-primary-500 focus:ring-primary-500"
                                    />
                                    <span>{reason}</span>
                                </label>
                            ))}

                            {selectedPresetReason === "기타 (직접 입력)" && (
                                <textarea
                                    value={customRejectReason}
                                    onChange={(e) => setCustomRejectReason(e.target.value)}
                                    placeholder="상세 거절 사유를 작성해주세요."
                                    rows={3}
                                    className="border border-achromatic-200 rounded-xl p-3 text-xs focus:outline-none focus:border-primary-500 w-full"
                                />
                            )}
                        </div>

                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={() => setRejectModalTarget(null)}
                                className="flex-1 py-3 rounded-xl text-xs font-bold bg-achromatic-100 text-achromatic-600 hover:bg-achromatic-200 transition-colors"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleConfirmReject}
                                className="flex-1 py-3 rounded-xl text-xs font-bold bg-red-500 text-white hover:bg-red-600 transition-colors shadow-sm"
                            >
                                거절 확정 (환불 처리)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawManageComponent;